import { useCallback, useEffect, useRef, useState } from "react";
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import api from "../../services/api";
import { useAuthContext } from "../../contexts/AuthContext";

const pathFor = path => `/api/industrial${path.startsWith("/") ? path : `/${path}`}`;
export const industrialApi = {
  get: (path, config) => api.get(pathFor(path), config).then(r => r.data),
  post: (path, body, config) => api.post(pathFor(path), body, config).then(r => r.data),
  put: (path, body, config) => api.put(pathFor(path), body, config).then(r => r.data),
  delete: (path, config) => api.delete(pathFor(path), config).then(r => r.data),
};
export function industrialError(error) {
  const status = error?.response?.status, data = error?.response?.data;
  if (status === 503) return data?.message || data?.mensagem || "A estrutura industrial ainda não está disponível nesta API. Aplique o SQL de estrutura e reinicie a API atualizada.";
  if (status === 403) return "Seu perfil não tem autorização para esta operação. Solicite acesso no controle de permissões.";
  if (status === 409) return data?.message || data?.mensagem || "Este registro foi atualizado por outra pessoa. Atualize a tela antes de tentar novamente.";
  if (!error?.response && error?.code !== "ERR_CANCELED") return "Não foi possível conectar à API. Confira a conexão e tente novamente.";
  const fields = data?.errors && Object.values(data.errors).flat().join(" ");
  return fields || data?.message || data?.mensagem || data?.detail || (typeof data === "string" ? data : null) || error?.message || "Não foi possível concluir a operação.";
}

const listeners = new Set();
let hub = null, hubToken = null, state = "connecting", generation = 0;
function publish(invalidate = false) { listeners.forEach(cb => cb(state, invalidate)); }
function connect() {
  const token = localStorage.getItem("token");
  if (hub && token === hubToken) return;
  const previous = hub; hub = null;
  if (previous) previous.stop().catch(() => {});
  hubToken = token;
  const current = ++generation;
  if (!token) { state = "offline"; publish(); return; }
  const base = new URL(api.defaults.baseURL || window.location.origin, window.location.origin);
  const connection = new HubConnectionBuilder().withUrl(`${base.origin}/hub/industrial`, {
    accessTokenFactory: () => localStorage.getItem("token") || "",
  }).withAutomaticReconnect([0, 2000, 5000, 15000]).configureLogging(LogLevel.None).build();
  hub = connection; state = "connecting"; publish();
  connection.on("AtualizacaoIndustrial", () => { if (generation === current) publish(true); });
  connection.onreconnecting(() => { if (generation === current) { state = "reconnecting"; publish(); } });
  connection.onreconnected(() => { if (generation === current) { state = "online"; publish(true); } });
  connection.onclose(() => { if (generation === current) { state = "offline"; publish(); hub = null; } });
  connection.start().then(() => { if (generation === current) { state = "online"; publish(true); } })
    .catch(() => { if (generation === current) { state = "offline"; publish(); hub = null; } });
}

export default function useIndustrialResource(path, params = {}) {
  const { user, isAuthenticated } = useAuthContext();
  const scope = `${user?.id || user?.usuarioId || user?.sub || ""}:${user?.entidadeId || user?.entidadeid || ""}:${user?.unidadeId || user?.unidadeid || ""}:${user?.perfilId || ""}`;
  const query = JSON.stringify(params);
  const [data, setData] = useState(null), [loading, setLoading] = useState(true), [error, setError] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null), [connection, setConnection] = useState("connecting");
  const request = useRef(null), sequence = useRef(0), latest = useRef(null);
  const reload = useCallback(async () => {
    if (!path) { setLoading(false); return; }
    if (request.current) request.current.abort();
    const controller = new AbortController(); request.current = controller;
    const n = ++sequence.current;
    setLoading(true);
    try {
      const value = await industrialApi.get(path, { params: JSON.parse(query), signal: controller.signal });
      if (sequence.current === n && !controller.signal.aborted) { setData(value); setError(""); setUpdatedAt(new Date()); }
    } catch (e) {
      if (sequence.current === n && !controller.signal.aborted) {
        if ([401,403].includes(e.response?.status)) { setData(null); setUpdatedAt(null); }
        setError(industrialError(e));
      }
    }
    finally { if (sequence.current === n && !controller.signal.aborted) setLoading(false); }
  }, [path, query, scope]);
  latest.current = reload;
  useEffect(() => { setData(null); setError(""); setUpdatedAt(null); reload(); return () => { ++sequence.current; request.current?.abort(); }; }, [reload]);
  useEffect(() => {
    if (!path || isAuthenticated === false) return undefined;
    let timer;
    const listener = (value, invalidate) => { setConnection(value); if (invalidate) { clearTimeout(timer); timer = setTimeout(() => latest.current?.(), 250); } };
    listeners.add(listener); connect(); setConnection(state);
    // Reconciliação quando um evento se perde ou o canal está indisponível.
    const poll = setInterval(() => { if (!document.hidden) { connect(); latest.current?.(); } }, 30000);
    const visible = () => { if (!document.hidden) { connect(); latest.current?.(); } };
    window.addEventListener("focus", visible);
    return () => { clearTimeout(timer); clearInterval(poll); window.removeEventListener("focus", visible); listeners.delete(listener); if (!listeners.size) { ++generation; hub?.stop().catch(() => {}); hub = null; } };
  }, [path, scope, isAuthenticated]);
  return { data, loading, error, reload, updatedAt, connection, scope };
}
